import React, {
  useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { parse, isValid } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Icon from '../../../../../shared/_TBComponents/Icon';
import NavigatorManageTable, {
  OverflowCell,
  useActionMenu,
  ActionMenuWrapper,
  ActionMenuButton,
  ActionMenuContainer,
  ActionMenuDivider,
  ActionMenuItemButton,
} from './NavigatorManageTable';

const TabsRow = styled.div`
  display: flex;
  margin: 0 16px 16px 16px;
  box-sizing: border-box;
  flex-shrink: 0;
  border-bottom: 1px solid #D6D6D6;
`;

const TabButton = styled.button`
  padding-bottom: 8px;
  margin-right: 16px;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  color: #1A1A1A;
  border: none;
  border-bottom: 3px solid ${(props) => (props.active ? '#1787FB' : 'transparent')};
  background: none;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: #1A1A1A;
  }
`;

const ArchivedActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const ArchivedActionsMobileWrapper = styled.div`
  display: none;

  @media screen and (max-width: 767px) {
    display: flex;
  }
`;

const ArchivedTableWidthOverride = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media screen and (max-width: 767px) {
    [data-column-id="actions"] {
      width: 100px !important;
      min-width: 100px !important;
      flex: none !important;
    }
  }
`;

const UserCountLink = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 4px;
  color: #77A7FF;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(119, 167, 255, 0.2);
  }
`;

// Calendar-Specific Cell Components

// Jumps to Manage Athletes pre-filtered to this calendar
const UserCountCell = ({ row }) => {
  const history = useHistory();
  return (
    <UserCountLink
      onClick={(e) => {
        e.stopPropagation();
        history.push('/dashboard/manage-athletes', {
          filterCalendarId: row.original.id,
          backPath: '/dashboard/manage-calendars',
          backLabel: 'Back to Manage Calendars',
        });
      }}
    >
      {row.original.userCount ?? 0}
    </UserCountLink>
  );
};

UserCountCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      userCount: PropTypes.number,
    }),
  }).isRequired,
};

const NameCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  padding-left: ${(props) => (props.isChild ? '36px' : '0')};
`;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  margin: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #1A1A1A;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(119, 167, 255, 0.15);
    color: #77A7FF;
  }

  &:disabled {
    cursor: default;
    color: #D6D6D6;

    &:hover {
      background: none;
    }
  }
`;

const ArrowIconWrapper = styled.span`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(props) => (props.isExpanded ? 90 : 0)}deg);

  svg {
    display: block;
  }
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Expand toggle on parent cal rows with subs and indent on those without
const NameCell = ({ row }) => {
  const {
    id, name, hasChildren, isChild, isCollapsed, isSearchActive, onToggleExpand,
  } = row.original;

  return (
    <NameCellWrapper isChild={isChild}>
      {!isChild && hasChildren && (
        <ExpandToggle
          type='button'
          disabled={isSearchActive}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(id);
          }}
        >
          <ArrowIconWrapper isExpanded={!isCollapsed}>
            <Icon icon='altrightarrow' size={15} color='currentColor' />
          </ArrowIconWrapper>
        </ExpandToggle>
      )}
      {isChild && (
        <Icon
          icon='sub-arrow'
          size={13}
          color='#1A1A1A'
          styledProps={{ flexShrink: 0, paddingBottom: '6px' }}
        />
      )}
      <NameText>{name}</NameText>
    </NameCellWrapper>
  );
};

NameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      hasChildren: PropTypes.bool,
      isChild: PropTypes.bool,
      isCollapsed: PropTypes.bool,
      isSearchActive: PropTypes.bool,
      onToggleExpand: PropTypes.func,
    }),
  }).isRequired,
};

const ActionDropdownCell = ({ row }) => {
  const history = useHistory();
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();

  return (
    <ActionMenuWrapper onClick={(e) => e.stopPropagation()}>
      <ActionMenuButton ref={buttonRef} type='button' onClick={openMenu}>
        <Icon icon='dots' size={16} color='currentColor' />
      </ActionMenuButton>
      {isOpen && createPortal(
        <ActionMenuContainer ref={menuRef} style={menuStyle}>
          <ActionMenuItemButton
            type='button'
            onClick={() => {
              const { id, parentCalendarId } = row.original;
              const buildHref = parentCalendarId
                ? `/build?parentCalendarId=${parentCalendarId}&childCalendarId=${id}`
                : `/build?calendarId=${id}`;
              history.push(buildHref);
              close();
            }}
          >
            <Icon icon='build' size={16} color='#1A1A1A' />
            Build
          </ActionMenuItemButton>
          <ActionMenuItemButton type='button' onClick={() => { row.original.onEdit(row.original); close(); }}>
            <Icon icon='edit-workout' size={16} color='#1A1A1A' />
            Edit
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' onClick={() => { row.original.onArchive(row.original); close(); }}>
            <Icon icon='archive' size={16} color='#1A1A1A' />
            Archive
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' onClick={() => { row.original.onDelete(row.original); close(); }}>
            <Icon icon='delete' size={16} color='#FF0505' />
            Delete
          </ActionMenuItemButton>
        </ActionMenuContainer>,
        document.body,
      )}
    </ActionMenuWrapper>
  );
};

ActionDropdownCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      parentCalendarId: PropTypes.number,
      onEdit: PropTypes.func,
      onArchive: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

// Dummy active parent cal rows for archived orphan sub cals
const ArchivedActionsCell = ({ row }) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();

  if (row.original.generic) return null;

  return (
    <>
      <ArchivedActionsWrapper onClick={(e) => e.stopPropagation()}>
        <Button
          name='Restore'
          icon='restore'
          iconColor='#FFF'
          iconSize={13}
          bgColor='#1A1A1A'
          borderColor='#1A1A1A'
          txtColor='#FFF'
          fontSize='14px'
          customBorderRadius='4px'
          styledWrapperProps={{ padding: '8px 16px' }}
          styledTextProps={{ fontWeight: 700 }}
          onClick={() => row.original.onRestore(row.original)}
        />
        <Button
          name='Delete'
          icon='delete'
          iconColor='#FF0505'
          iconSize={13}
          bgColor='#FFF'
          txtColor='#1A1A1A'
          fontSize='14px'
          customBorderRadius='4px'
          styledWrapperProps={{ padding: '8px 16px', border: '2px solid #EEEEEE' }}
          styledTextProps={{ fontWeight: 700 }}
          onClick={() => row.original.onDelete(row.original)}
        />
      </ArchivedActionsWrapper>
      <ArchivedActionsMobileWrapper onClick={(e) => e.stopPropagation()}>
        <ActionMenuWrapper>
          <ActionMenuButton ref={buttonRef} type='button' onClick={openMenu}>
            <Icon icon='dots' size={16} color='currentColor' />
          </ActionMenuButton>
          {isOpen && createPortal(
            <ActionMenuContainer ref={menuRef} style={menuStyle}>
              <ActionMenuItemButton
                type='button'
                onClick={() => { row.original.onRestore(row.original); close(); }}
              >
                <Icon icon='restore' size={16} color='#1A1A1A' />
                Restore
              </ActionMenuItemButton>
              <ActionMenuDivider />
              <ActionMenuItemButton
                type='button'
                onClick={() => { row.original.onDelete(row.original); close(); }}
              >
                <Icon icon='delete' size={16} color='#FF0505' />
                Delete
              </ActionMenuItemButton>
            </ActionMenuContainer>,
            document.body,
          )}
        </ActionMenuWrapper>
      </ArchivedActionsMobileWrapper>
    </>
  );
};

ArchivedActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      generic: PropTypes.bool,
      onRestore: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

// createdAt is a display string ("MMMM d, yyyy"), not sortable as text - parse it first.
const toComparableDate = (value) => {
  if (!value || value === '-') return null;
  const parsed = parse(value, 'MMMM d, yyyy', new Date());
  return isValid(parsed) ? parsed.getTime() : null;
};

const compareValues = (a, b, columnId, desc) => {
  if (columnId === 'createdAt') {
    const timeA = toComparableDate(a);
    const timeB = toComparableDate(b);
    if (timeA === null && timeB === null) return 0;
    // Missing dates always sort last, countering react-table's desc flip below.
    if (timeA === null) return desc ? -1 : 1;
    if (timeB === null) return desc ? 1 : -1;
    return timeA - timeB;
  }
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const aStr = (a ?? '').toString().toLowerCase();
  const bStr = (b ?? '').toString().toLowerCase();
  if (aStr < bStr) return -1;
  if (aStr > bStr) return 1;
  return 0;
};

// Groups by groupId (parent's real id); groupSortValues orders groups by the parent's own value.
const groupedSort = (rowA, rowB, columnId, desc) => {
  const groupIdA = rowA.original.groupId;
  const groupIdB = rowB.original.groupId;

  if (groupIdA !== groupIdB) {
    const groupCompare = compareValues(
      rowA.original.groupSortValues?.[columnId],
      rowB.original.groupSortValues?.[columnId],
      columnId,
      desc,
    );
    if (groupCompare !== 0) return groupCompare;
    // Tiebreak so groups with an identical value don't interleave.
    return compareValues(groupIdA, groupIdB);
  }

  if (rowA.original.sortOrderIndex !== rowB.original.sortOrderIndex) {
    const diff = rowA.original.sortOrderIndex - rowB.original.sortOrderIndex;
    // Counter-flip so parent-before-child holds regardless of desc.
    return desc ? -diff : diff;
  }

  return compareValues(rowA.values[columnId], rowB.values[columnId], columnId, desc);
};

// Main Component

const ManageCalendarsTable = React.forwardRef(({
  isArchived, onToggleArchived, onEditCalendar, onArchiveCalendar, onDeleteCalendar,
  onRestoreCalendar, onBulkAction, onVisibleCalendarsChange,
}, ref) => {
  const innerRef = useRef(null);
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const calendarsRaw = useSelector((state) => state.calendars.data.unparsedCalendars);
  const isLoadingCalendars = useSelector((state) => state.calendars.ui.isCalendarsLoading);

  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  const isSearchActive = searchQuery.trim().length > 0;

  const toggleExpand = useCallback((id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // IDs including dummy archived-group IDs that can be expanded or collapsed
  const expandableIds = useMemo(() => {
    const ids = calendarsRaw
      .filter((c) => (c.childCalendars || []).some((child) => !!child.archived === !!c.archived))
      .map((c) => c.id);

    const genericIds = calendarsRaw
      .filter((c) => !c.archived && (c.childCalendars || []).some((child) => child.archived))
      .map((c) => -c.id);

    return ids.concat(genericIds);
  }, [calendarsRaw]);

  const handleExpandAll = useCallback(() => {
    setCollapsedIds(new Set());
  }, []);

  const handleCollapseAll = useCallback(() => {
    setCollapsedIds(new Set(expandableIds));
  }, [expandableIds]);

  // Builds parent/child rows and hides collapsed children unless searching
  const buildRows = (archived, collapsedSet) => {
    const rows = [];
    calendarsRaw.forEach((calendar) => {
      if (!!calendar.archived !== archived) return;
      const children = (calendar.childCalendars || []).filter((c) => !!c.archived === archived);
      const isCollapsed = collapsedSet.has(calendar.id);

      // Shared by the parent row and its children so sorting groups by the parent's value.
      const groupSortValues = {
        displayName: calendar.name,
        userCount: calendar.userCount,
        workoutVisibilityDescription: calendar.workoutVisibilityDescription,
        createdAt: calendar.createdAt || '-',
      };

      rows.push({
        ...calendar,
        displayName: calendar.name,
        parentName: calendar.name,
        sortOrderIndex: 0,
        hasChildren: children.length > 0,
        isChild: false,
        isCollapsed,
        isSearchActive,
        groupId: calendar.id,
        groupSortValues,
      });

      if (children.length === 0 || (isCollapsed && !isSearchActive)) return;
      children.forEach((child) => {
        rows.push({
          ...child,
          displayName: child.name,
          parentName: calendar.name,
          sortOrderIndex: 1,
          hasChildren: false,
          isChild: true,
          isSearchActive,
          groupId: calendar.id,
          groupSortValues,
        });
      });
    });

    // Groups archived children of active parents under a dummy parent row
    if (archived) {
      calendarsRaw.forEach((calendar) => {
        if (calendar.archived) return;
        const archivedChildren = (calendar.childCalendars || []).filter((c) => c.archived);
        if (archivedChildren.length === 0) return;

        const genericId = -calendar.id;
        const genericName = `Active Parent (${calendar.name})`;
        const isCollapsed = collapsedSet.has(genericId);
        const groupSortValues = { displayName: genericName };

        rows.push({
          id: genericId,
          name: genericName,
          displayName: genericName,
          parentName: genericName,
          sortOrderIndex: 0,
          hasChildren: true,
          isChild: false,
          isCollapsed,
          isSearchActive,
          generic: true,
          groupId: genericId,
          groupSortValues,
        });

        if (isCollapsed && !isSearchActive) return;
        archivedChildren.forEach((child) => {
          rows.push({
            ...child,
            displayName: child.name,
            parentName: genericName,
            sortOrderIndex: 1,
            hasChildren: false,
            isChild: true,
            isSearchActive,
            groupId: genericId,
            groupSortValues,
          });
        });
      });
    }

    return rows;
  };

  // Searches both parent and child cals to keep related matches together
  const calendarSearchFn = useCallback((row, q) => {
    const matchesSelf = (row.name || '').toLowerCase().includes(q)
      || (row.workoutVisibilityDescription || '').toLowerCase().includes(q);
    if (matchesSelf) return true;
    if (row.isChild) return false;
    return (row.childCalendars || []).some((child) => (child.name || '').toLowerCase().includes(q));
  }, []);

  const tableData = useMemo(
    () => buildRows(false, collapsedIds).map((calendar) => ({
      ...calendar,
      onEdit: onEditCalendar,
      onArchive: onArchiveCalendar,
      onDelete: onDeleteCalendar,
      onToggleExpand: toggleExpand,
    })),
    [calendarsRaw, collapsedIds, isSearchActive, onEditCalendar, onArchiveCalendar,
      onDeleteCalendar, toggleExpand],
  );

  const archivedTableData = useMemo(
    () => buildRows(true, collapsedIds).map((calendar) => ({
      ...calendar,
      onRestore: onRestoreCalendar,
      onDelete: onDeleteCalendar,
      onToggleExpand: toggleExpand,
    })),
    [calendarsRaw, collapsedIds, isSearchActive, onRestoreCalendar, onDeleteCalendar, toggleExpand],
  );

  // Lets the parent call scrollToCalendar(id) via a ref, from CalendarNavigator's Manage button.
  useImperativeHandle(ref, () => ({
    scrollToCalendar: (id) => {
      if (isArchived) onToggleArchived(false);
      setPendingScrollId(id);
    },
  }), [isArchived, onToggleArchived]);

  // Expands a collapsed parent first if needed, then scrolls once the target row exists.
  useEffect(() => {
    if (pendingScrollId == null || isArchived) return;
    const isVisible = tableData.some((row) => row.id === pendingScrollId);
    if (!isVisible) {
      const parent = calendarsRaw.find(
        (c) => (c.childCalendars || []).some((child) => child.id === pendingScrollId),
      );
      if (parent && collapsedIds.has(parent.id)) {
        toggleExpand(parent.id);
        return;
      }
      setPendingScrollId(null);
      return;
    }
    innerRef.current?.scrollToRow(pendingScrollId);
    setPendingScrollId(null);
  }, [pendingScrollId, isArchived, tableData, calendarsRaw, collapsedIds, toggleExpand]);

  const columns = useMemo(() => [
    {
      Header: 'Name',
      accessor: 'displayName',
      sortType: groupedSort,
      headerStyle: { minWidth: '260px', flex: '1' },
      cellStyle: { minWidth: '260px', flex: '1', overflow: 'hidden' },
      Cell: NameCell,
    },
    {
      Header: 'Users',
      accessor: 'userCount',
      sortType: groupedSort,
      headerStyle: { width: '110px', textAlign: 'left' },
      cellStyle: { width: '110px', textAlign: 'left' },
      Cell: UserCountCell,
    },
    {
      Header: 'Visibility',
      accessor: 'workoutVisibilityDescription',
      sortType: groupedSort,
      headerStyle: { width: '200px', textAlign: 'left' },
      cellStyle: { width: '200px', textAlign: 'left' },
      Cell: OverflowCell,
    },
    {
      Header: 'Date Created',
      id: 'createdAt',
      accessor: (row) => row.createdAt || '-',
      sortType: groupedSort,
      headerStyle: { width: '160px', textAlign: 'left' },
      cellStyle: { width: '160px', textAlign: 'left' },
      Cell: OverflowCell,
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '100px', textAlign: 'center' },
      cellStyle: { width: '100px', textAlign: 'center' },
      Cell: ActionDropdownCell,
    },
  ], []);

  const archivedColumns = useMemo(() => [
    {
      Header: 'Name',
      accessor: 'displayName',
      sortType: groupedSort,
      headerStyle: { minWidth: '300px', flex: '1' },
      cellStyle: { minWidth: '300px', flex: '1', overflow: 'hidden' },
      Cell: NameCell,
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '260px', textAlign: 'center' },
      cellStyle: { width: '260px', textAlign: 'center' },
      Cell: ArchivedActionsCell,
    },
  ], []);

  const calendarBulkActions = useMemo(() => [
    {
      key: 'archiveCalendars', label: 'Archive', icon: 'archive', iconColor: '#1A1A1A',
    },
    {
      key: 'deleteCalendars', label: 'Delete', icon: 'delete', iconColor: '#FF0505', dividerBefore: true,
    },
  ], []);

  const archivedCalendarBulkActions = useMemo(() => [
    {
      key: 'restoreCalendars', label: 'Restore', icon: 'restore', iconColor: '#1A1A1A',
    },
    {
      key: 'deleteArchivedCalendars', label: 'Delete', icon: 'delete', iconColor: '#FF0505', dividerBefore: true,
    },
  ], []);

  return (
    <>
      <TabsRow>
        <TabButton
          type='button'
          active={!isArchived}
          onClick={() => onToggleArchived(false)}
        >
          Active Calendars
        </TabButton>
        <TabButton
          type='button'
          active={isArchived}
          onClick={() => onToggleArchived(true)}
        >
          Archived
        </TabButton>
      </TabsRow>

      {isArchived ? (
        <ArchivedTableWidthOverride>
          <NavigatorManageTable
            key='archived'
            data={archivedTableData}
            columns={archivedColumns}
            isLoading={isLoadingCalendars}
            searchFn={calendarSearchFn}
            searchPlaceholder='Search for a calendar'
            emptyMessage='No archived calendars.'
            searchEmptyMessage='No archived calendars match your search.'
            entityLabel='calendar'
            defaultSortBy={[{ id: 'displayName', desc: false }]}
            bulkActions={archivedCalendarBulkActions}
            onBulkAction={onBulkAction}
            isRowSelectable={(row) => !row.generic}
            onFilteredDataChange={onVisibleCalendarsChange}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            extraActionContent={expandableIds.length > 0 && (
              <>
                <Button
                  name='Expand All'
                  bgColor='#FFF'
                  borderColor='#D6D6D6'
                  txtColor='#1A1A1A'
                  customBorderRadius='4px'
                  fontSize='14px'
                  styledTextProps={{ fontWeight: 700 }}
                  styledWrapperProps={{ height: '40px', padding: '0 14px' }}
                  onClick={handleExpandAll}
                />
                <Button
                  name='Collapse All'
                  bgColor='#FFF'
                  borderColor='#D6D6D6'
                  txtColor='#1A1A1A'
                  customBorderRadius='4px'
                  fontSize='14px'
                  styledTextProps={{ fontWeight: 700 }}
                  styledWrapperProps={{ height: '40px', padding: '0 14px' }}
                  onClick={handleCollapseAll}
                />
              </>
            )}
            virtualizeRows
          />
        </ArchivedTableWidthOverride>
      ) : (
        <NavigatorManageTable
          key='active'
          ref={innerRef}
          data={tableData}
          columns={columns}
          isLoading={isLoadingCalendars}
          searchFn={calendarSearchFn}
          searchPlaceholder='Search for a calendar'
          emptyMessage='No calendars found.'
          searchEmptyMessage='No calendars match your search.'
          bulkActions={calendarBulkActions}
          onBulkAction={onBulkAction}
          entityLabel='calendar'
          extraActionContent={expandableIds.length > 0 && (
            <>
              <Button
                name='Expand All'
                bgColor='#FFF'
                borderColor='#D6D6D6'
                txtColor='#1A1A1A'
                customBorderRadius='4px'
                fontSize='14px'
                styledTextProps={{ fontWeight: 700 }}
                styledWrapperProps={{ height: '40px', padding: '0 14px' }}
                onClick={handleExpandAll}
              />
              <Button
                name='Collapse All'
                bgColor='#FFF'
                borderColor='#D6D6D6'
                txtColor='#1A1A1A'
                customBorderRadius='4px'
                fontSize='14px'
                styledTextProps={{ fontWeight: 700 }}
                styledWrapperProps={{ height: '40px', padding: '0 14px' }}
                onClick={handleCollapseAll}
              />
            </>
          )}
          defaultSortBy={[{ id: 'displayName', desc: false }]}
          onFilteredDataChange={onVisibleCalendarsChange}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          virtualizeRows
        />
      )}
    </>
  );
});

ManageCalendarsTable.displayName = 'ManageCalendarsTable';

ManageCalendarsTable.propTypes = {
  isArchived: PropTypes.bool,
  onToggleArchived: PropTypes.func.isRequired,
  onEditCalendar: PropTypes.func.isRequired,
  onArchiveCalendar: PropTypes.func.isRequired,
  onDeleteCalendar: PropTypes.func.isRequired,
  onRestoreCalendar: PropTypes.func.isRequired,
  onBulkAction: PropTypes.func.isRequired,
  onVisibleCalendarsChange: PropTypes.func,
};

ManageCalendarsTable.defaultProps = {
  isArchived: false,
  onVisibleCalendarsChange: () => {},
};

export default ManageCalendarsTable;
